import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Sparkles, Shield, Zap, Globe, Users, 
  TrendingUp, Award, Target, Rocket, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';

// Featured categories to highlight
const FEATURED_CATEGORIES = [
  'AI Chatbots', 'AI Writing', 'AI Image Generation', 'AI Video', 
  'AI Voice & Audio', 'AI Marketing', 'AI Business', 'AI Automation',
  'AI Analytics', 'AI Education', 'AI Health & Wellness', 'AI Finance',
  'AI Productivity', 'AI Social Media', 'AI E-commerce', 'AI Customer Service'
];

function AboutUs() {
  const navigate = useNavigate();

  useEffect(() => {
    // scroll handled by <ScrollRestoration />
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#00F0FF] hover:text-[#00C2CC]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Marketplace
            </Button>
            <Logo />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00F0FF]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white text-black/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-6">
              <Heart className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-sm text-[#00F0FF] font-medium">Our Story</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Empowering{' '}
              <span className="bg-gradient-to-r from-[#00F0FF] to-white bg-clip-text text-transparent">
                AI Creators
              </span>
              {' '}to Change the World
            </h1>
            
            <p className="text-lg md:text-xl text-neutral-400 max-w-3xl mx-auto">
              theailedger.ai is the premier marketplace where innovative AI creators connect 
              with businesses and individuals seeking cutting-edge AI solutions. We believe 
              everyone should have access to the power of artificial intelligence.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Our Mission
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-6">
                We&apos;re building the future of AI commerce. Our platform empowers creators, 
                entrepreneurs, and innovators to monetize their AI products while giving 
                buyers instant access to tools that transform how they work and live.
              </p>
              <p className="text-neutral-400 text-lg leading-relaxed">
                Whether you&apos;re an AI developer looking to reach a global audience or a 
                business seeking the perfect AI solution, theailedger.ai is your destination.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
                  <Target className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <h3 className="font-bold text-2xl mb-1">52+</h3>
                <p className="text-neutral-400 text-sm">AI Categories</p>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-white text-black/10 flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-2xl mb-1">Global</h3>
                <p className="text-neutral-400 text-sm">Marketplace</p>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <h3 className="font-bold text-2xl mb-1">Instant</h3>
                <p className="text-neutral-400 text-sm">Delivery</p>
              </div>
              <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B6B]/10 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-[#FF6B6B]" />
                </div>
                <h3 className="font-bold text-2xl mb-1">Secure</h3>
                <p className="text-neutral-400 text-sm">Payments</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-neutral-900/30">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why{' '}
              <span className="text-[#00F0FF]">theailedger.ai</span>
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">
              We&apos;re not just another marketplace. We&apos;re a community dedicated to 
              advancing AI innovation and making it accessible to everyone.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 hover:border-[#00F0FF]/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-[#00F0FF]" />
              </div>
              <h3 className="text-xl font-bold mb-3">For Creators</h3>
              <p className="text-neutral-400">
                Turn your AI innovations into income. Set your prices, reach a global 
                audience, and get paid instantly with every sale. Our affiliate program 
                helps you grow even faster.
              </p>
            </div>
            
            <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 hover:border-white/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">For Buyers</h3>
              <p className="text-neutral-400">
                Discover cutting-edge AI tools across 52 categories. From chatbots to 
                image generators, find the perfect solution for your needs with instant 
                download access.
              </p>
            </div>
            
            <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 hover:border-[#00F0FF]/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-[#00F0FF]" />
              </div>
              <h3 className="text-xl font-bold mb-3">For Affiliates</h3>
              <p className="text-neutral-400">
                Earn commissions by promoting AI products you love. Our instant payout 
                system means you get paid immediately when your referrals make a purchase.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Showcase */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Explore Our Categories
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">
              From productivity tools to creative AI, we cover every aspect of 
              artificial intelligence innovation.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {FEATURED_CATEGORIES.map((category, index) => (
              <button 
                key={index}
                onClick={() => {
                  navigate(`/?category=${encodeURIComponent(category)}`)
                }}
                className="px-4 py-2 rounded-full bg-neutral-900/50 border border-white/10 text-sm text-neutral-300 hover:border-[#00F0FF]/30 hover:text-[#00F0FF] transition-colors cursor-pointer"
              >
                {category}
              </button>
            ))}
            <button 
              onClick={() => {
                navigate('/')
              }}
              className="px-4 py-2 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-sm text-[#00F0FF] font-medium cursor-pointer hover:bg-[#00F0FF]/20 transition-colors"
            >
              +36 more categories
            </button>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-neutral-900/30">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Values</h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-8 h-8 text-[#00F0FF]" />
              </div>
              <h3 className="font-bold mb-2">Innovation</h3>
              <p className="text-neutral-400 text-sm">Pushing the boundaries of what&apos;s possible with AI</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-white text-black/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold mb-2">Trust</h3>
              <p className="text-neutral-400 text-sm">Secure transactions and verified creators</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-[#00F0FF]" />
              </div>
              <h3 className="font-bold mb-2">Accessibility</h3>
              <p className="text-neutral-400 text-sm">AI tools for everyone, everywhere</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#FF6B6B]/10 flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-[#FF6B6B]" />
              </div>
              <h3 className="font-bold mb-2">Quality</h3>
              <p className="text-neutral-400 text-sm">Curated products that deliver results</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-[#00F0FF]/10 to-white/10 border border-white/10 rounded-3xl p-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-neutral-400 mb-8 max-w-2xl mx-auto">
              Join theailedger.ai today and be part of the AI revolution. 
              Whether you&apos;re buying or selling, your journey starts here.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  navigate('/')
                }}
                className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold px-8 py-6 rounded-full text-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Browse Products
              </Button>
              <Button
                onClick={() => {
                  navigate('/contact')
                }}
                variant="outline"
                className="border-white/20 hover:bg-white/5 px-8 py-6 rounded-full text-lg"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <p className="text-neutral-500 text-sm">
            © 2026 theailedger.ai - The Marketplace for AI Products
          </p>
        </div>
      </footer>
    </div>
  );
}

export default AboutUs;
